import React, { useId, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { createSupportTicket } from '../../api/supportTicket';
import { TTAI_API_BASE_URL, TTAI_HELPDESK_ID, DEPLOYMENT_KEY } from '../../config';
import packageJson from '../../../package.json';

const appVersion = packageJson.version;

const EMPTY_FORM = { title: '', description: '', contactEmail: '' };

function SupportWidget() {
  const { t } = useTranslation('supportWidget');
  const [searchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const titleId = useId();
  const descriptionId = useId();
  const emailId = useId();

  const kiosk = searchParams.get('kiosk') === '1';
  const configured = Boolean(TTAI_HELPDESK_ID && TTAI_API_BASE_URL);

  if (kiosk || !configured) {
    return null;
  }

  const handleClose = () => {
    setIsOpen(false);
    setForm(EMPTY_FORM);
    setError(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    const deviceSerial = searchParams.get('device') || undefined;
    const description = `[App version: ${appVersion}]\n\n${form.description}`;

    try {
      await createSupportTicket({
        apiBaseUrl: TTAI_API_BASE_URL,
        helpDeskId: TTAI_HELPDESK_ID,
        title: form.title,
        description,
        contactEmail: form.contactEmail || undefined,
        deploymentKey: DEPLOYMENT_KEY || undefined,
        deviceSerial,
      });
      toast.success(t('success'));
      handleClose();
    } catch (err) {
      setError(t('error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 z-40 rounded-full bg-primary-600 px-4 py-3 text-sm font-medium text-white shadow-lg hover:bg-primary-700"
      >
        {t('button')}
      </button>

      {isOpen && (
        <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
          <DialogBackdrop className="fixed inset-0 bg-gray-500 bg-opacity-75" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <DialogPanel className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
              <DialogTitle className="text-lg font-medium text-gray-900">{t('title')}</DialogTitle>

              <form onSubmit={handleSubmit} className="mt-4 space-y-3">
                <div>
                  <label htmlFor={titleId} className="block text-sm font-medium text-gray-700">
                    {t('fieldTitle')}
                  </label>
                  <input
                    id={titleId}
                    type="text"
                    required
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label htmlFor={descriptionId} className="block text-sm font-medium text-gray-700">
                    {t('fieldDescription')}
                  </label>
                  <textarea
                    id={descriptionId}
                    required
                    rows={4}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label htmlFor={emailId} className="block text-sm font-medium text-gray-700">
                    {t('fieldEmail')}
                  </label>
                  <input
                    id={emailId}
                    type="email"
                    value={form.contactEmail}
                    onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
                    className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>

                {error && <p className="text-sm text-red-600">{error}</p>}

                <div className="flex justify-end space-x-3 pt-2">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={submitting}
                    className="rounded px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    {t('cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="rounded bg-primary-600 px-3 py-2 text-sm text-white hover:bg-primary-700 disabled:opacity-50"
                  >
                    {t('submit')}
                  </button>
                </div>
              </form>
            </DialogPanel>
          </div>
        </Dialog>
      )}
    </>
  );
}

export default SupportWidget;
